<template>
  <form class="form-horizontal" method="post" @submit.prevent="submit">
    <div class="form-group m-b-20" :class="{ 'has-error': usernameError }">
      <label for="cred-username">{{ trans("users.usernameLabel") }}</label>
      <input
        id="cred-username"
        ref="usernameInput"
        v-model="username"
        type="text"
        class="form-control"
        name="username"
        autocomplete="username"
        required
        :placeholder="trans('auth.usernameOrEmailPlaceholder')"
      />
      <span v-if="usernameError" data-test="login-error" class="help-block">
        <strong>{{ usernameError }}</strong>
      </span>
    </div>

    <div class="form-group m-b-20" :class="{ 'has-error': passwordError }">
      <label for="cred-password">{{ trans("auth.passwordLabel") }}</label>
      <div class="input-group">
        <input
          id="cred-password"
          v-model="password"
          :type="showPassword ? 'text' : 'password'"
          class="form-control"
          name="password"
          required
          autocomplete="current-password"
        />
        <div class="input-group-append">
          <button
            type="button"
            class="btn btn-default"
            data-test="login-password-toggle"
            @click="showPassword = !showPassword"
          >
            <i :class="showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
          </button>
        </div>
      </div>
      <span v-if="passwordError" data-test="login-error" class="help-block">
        <strong>{{ passwordError }}</strong>
      </span>
    </div>

    <div class="resend-btn mb-3">
      <router-link to="/auth/password/reset">
        {{ trans("auth.forgotPasswordLinkText") }}
      </router-link>
    </div>

    <div class="form-group account-btn text-center m-t-10">
      <clip-loader v-show="loading" />
      <button
        v-if="!loading"
        class="btn btn-custom btn-block btn-lg"
        type="submit"
        data-test="login-submit"
      >
        {{ submitLabel }}
      </button>
    </div>
  </form>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { lang } from "../../i18n";
import { practitionerHttpService } from "../../app/practitioner-portal/store/practitioner-http-service";
import ClipLoader from "../Utils/ClipLoader.vue";

const props = defineProps({
  method: { type: String, required: true },
});

const emit = defineEmits(["success"]);

const submitLabel = computed(() =>
  props.method === "username-password:sms"
    ? lang.get("auth.methodSmsTitle")
    : lang.get("auth.methodAuthAppTitle"),
);

const usernameInput = ref(null);
const username = ref("");
const password = ref("");
const showPassword = ref(false);
const loading = ref(false);
const usernameError = ref("");
const passwordError = ref("");

onMounted(() => usernameInput.value?.focus());

const submit = () => {
  usernameError.value = "";
  passwordError.value = "";
  loading.value = true;

  practitionerHttpService
    .post("/api/internal/auth/sms", {
      username: username.value,
      password: password.value,
      method: props.method,
    })
    .then((response) => {
      loading.value = false;
      if (response.data.token) {
        emit("success", response.data.token);
      }
    })
    .catch((error) => {
      loading.value = false;
      const status = error.response?.status;
      const data = error.response?.data;
      if (status === 422) {
        if (data.username) usernameError.value = data.username[0];
        if (data.password) {
          passwordError.value = data.password[0];
          password.value = "";
        }
      } else if (status === 429) {
        usernameError.value = data.key ?? "";
      }
    });
};
</script>
